import { useState, useEffect, useCallback } from 'react';
import { CreditCard, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import { api } from '../../api/client';

interface Credencial {
  id: string;
  codigo: string;
  descricao: string | null;
  ativa: boolean;
  criadoEm: string;
  aluno: {
    id: string;
    nome: string;
    matricula: string | null;
    escola: {
      id: string;
      nome: string;
      municipio: { id: string; nome: string } | null;
    } | null;
  } | null;
}

export function MasterCredenciaisPage() {
  const [items, setItems] = useState<Credencial[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [ativaFilter, setAtivaFilter] = useState('');
  const limite = 20;

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limite: String(limite) });
    if (ativaFilter) params.set('ativa', ativaFilter);

    api.get(`/master/dashboard/rfid/credenciais?${params}`)
      .then(r => {
        const data = r.data;
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, ativaFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(total / limite);

  const thStyle: React.CSSProperties = {
    padding: '10px 14px', fontSize: '0.75rem', fontWeight: 600,
    color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: '1px solid var(--border-color)',
    textAlign: 'left',
  };
  const tdStyle: React.CSSProperties = {
    padding: '12px 14px', fontSize: '0.85rem', color: 'var(--text-main)',
    borderBottom: '1px solid var(--border-color, #f1f5f9)',
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CreditCard size={22} style={{ color: '#8b5cf6' }} />
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
            Credenciais RFID
          </h1>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '2px 10px', borderRadius: 20 }}>
            {total} total
          </span>
        </div>

        <select
          value={ativaFilter}
          onChange={e => { setAtivaFilter(e.target.value); setPage(1); }}
          style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border-color)',
            background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.85rem',
            height: 34,
          }}
        >
          <option value="">Todas</option>
          <option value="true">Ativas</option>
          <option value="false">Inativas</option>
        </select>
      </div>

      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-color)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-sidebar, #f8fafc)' }}>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Codigo</th>
              <th style={thStyle}>Tipo</th>
              <th style={thStyle}>Aluno</th>
              <th style={thStyle}>Matricula</th>
              <th style={thStyle}>Escola</th>
              <th style={thStyle}>Municipio</th>
              <th style={thStyle}>Emitida em</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma credencial encontrada</td></tr>
            ) : items.map(c => (
              <tr key={c.id} style={{ transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover, #f8fafc)')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <td style={tdStyle}>
                  {c.ativa ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
                      background: '#ecfdf5', color: '#10b981',
                    }}>
                      <Check size={12} /> Ativa
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
                      background: '#fef2f2', color: '#ef4444',
                    }}>
                      <X size={12} /> Inativa
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{c.codigo}</td>
                <td style={tdStyle}>{c.descricao || '-'}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{c.aluno?.nome || '-'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.aluno?.matricula || '-'}</td>
                <td style={tdStyle}>{c.aluno?.escola?.nome || '-'}</td>
                <td style={tdStyle}>{c.aluno?.escola?.municipio?.nome || '-'}</td>
                <td style={{ ...tdStyle, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {new Date(c.criadoEm).toLocaleDateString('pt-BR')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-color)',
              background: 'var(--bg-card)', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Pagina {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-color)',
              background: 'var(--bg-card)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
